import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { generateCartId, generateWishlistId, getToken, setToken, removeToken, getUser, setUser, removeUser } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [user, setUserState] = useState(getUser());
  const [cart, setCart] = useState({ items: [], subtotal: 0, item_count: 0 });
  const [wishlist, setWishlist] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [cartId] = useState(generateCartId());
  const [wishlistId] = useState(generateWishlistId());

  // Auth functions
  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/auth/login`, { email, password });
    setToken(response.data.token);
    setUser(response.data.user);
    setUserState(response.data.user);
    return response.data;
  };

  const register = async (email, password, name, isAdmin = false) => {
    const response = await axios.post(`${API_URL}/auth/register`, { 
      email, 
      password, 
      name,
      is_admin: isAdmin 
    });
    setToken(response.data.token);
    setUser(response.data.user);
    setUserState(response.data.user);
    return response.data;
  };

  const logout = () => {
    removeToken();
    removeUser();
    setUserState(null);
  };

  // Cart functions
  const fetchCart = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/cart/${cartId}`);
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    }
  }, [cartId]);

  const addToCart = async (productId, quantity = 1, variantId = null) => {
    await axios.post(`${API_URL}/cart/${cartId}/add`, {
      product_id: productId,
      variant_id: variantId,
      quantity
    });
    await fetchCart();
  };

  const updateCartItem = async (productId, quantity, variantId = null) => {
    await axios.put(`${API_URL}/cart/${cartId}/update`, {
      product_id: productId,
      variant_id: variantId,
      quantity
    });
    await fetchCart();
  };

  const removeFromCart = async (productId, variantId = null) => {
    let url = `${API_URL}/cart/${cartId}/item/${productId}`;
    if (variantId) url += `?variant_id=${encodeURIComponent(variantId)}`;
    await axios.delete(url);
    await fetchCart();
  };

  const clearCart = async () => {
    await axios.delete(`${API_URL}/cart/${cartId}`);
    setCart({ items: [], subtotal: 0, item_count: 0 });
  };

  // Wishlist functions
  const fetchWishlist = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/wishlist/${wishlistId}`);
      setWishlist(response.data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    }
  }, [wishlistId]);

  const addToWishlist = async (productId) => {
    await axios.post(`${API_URL}/wishlist/${wishlistId}/add`, {
      product_id: productId
    });
    await fetchWishlist();
  };

  const removeFromWishlist = async (productId) => {
    await axios.delete(`${API_URL}/wishlist/${wishlistId}/item/${productId}`);
    await fetchWishlist();
  };

  const isInWishlist = (productId) => {
    return wishlist.items.some(item => item.id === productId);
  };

  // Auth header helper
  const getAuthHeaders = () => {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Initial data fetch
  useEffect(() => {
    fetchCart();
    fetchWishlist();
  }, [fetchCart, fetchWishlist]);

  const value = {
    user,
    cart,
    wishlist,
    cartId,
    wishlistId,
    loading,
    setLoading,
    login,
    register,
    logout,
    fetchCart,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    fetchWishlist,
    addToWishlist,
    removeFromWishlist,
    isInWishlist,
    getAuthHeaders,
    API_URL
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};
